import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Brain, Save, PlusCircle, Tag, RefreshCw, Code, Users, Building2, Wrench, Filter, Search, MapPin, Globe, GraduationCap, Book, Briefcase } from 'lucide-react';
import { skillsService } from '../services/api';
import { Alert, AlertDescription } from '../components/ui/alert';


/// Region and state data
const REGIONS_AND_STATES = {
  "Northeast": [
    "Connecticut", "Maine", "Massachusetts", "New Hampshire", 
    "New Jersey", "New York", "Pennsylvania", "Rhode Island", "Vermont"
  ],
  "Southeast": [
    "Alabama", "Arkansas", "Delaware", "Florida", "Georgia", 
    "Kentucky", "Louisiana", "Maryland", "Mississippi", 
    "North Carolina", "South Carolina", "Tennessee", 
    "Virginia", "West Virginia"
  ],
  "Midwest": [
    "Illinois", "Indiana", "Iowa", "Kansas", "Michigan", 
    "Minnesota", "Missouri", "Nebraska", "North Dakota", 
    "Ohio", "South Dakota", "Wisconsin"
  ],
  "Southwest": [
    "Arizona", "New Mexico", "Oklahoma", "Texas"
  ],
  "West": [
    "California", "Colorado", "Idaho", "Montana", "Nevada", 
    "Oregon", "Utah", "Washington", "Wyoming"
  ],
  "Other": [
    "Alaska", "Hawaii", "Puerto Rico", 
    "U.S. Virgin Islands", "American Samoa", 
    "Northern Mariana Islands", "Guam"
  ]
};

// Category icons mapping
const CATEGORY_ICONS = {
  "skills": <Wrench className="h-4 w-4" />,
  "assets": <Building2 className="h-4 w-4" />,
  "relationships": <Users className="h-4 w-4" />,
  "areas_of_knowledge": <Book className="h-4 w-4" />,
  "certifications": <Tag className="h-4 w-4" />,
  "degrees": <GraduationCap className="h-4 w-4" />
};

const SkillsExchange = () => {
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedSkillType, setSelectedSkillType] = useState('skills');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSkill, setSelectedSkill] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [availableSkills, setAvailableSkills] = useState({});
  const [filteredResults, setFilteredResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Reset dependent fields when parent selections change
  useEffect(() => {
    setSelectedState('');
  }, [selectedRegion]);

  useEffect(() => {
    setSelectedCategory('');
    setSelectedSkill('');
  }, [selectedSkillType]);

  useEffect(() => {
    setSelectedSkill('');
  }, [selectedCategory]);

  // Fetch skills data using skillsService
  useEffect(() => {
    const loadSkillsData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await skillsService.getSkillsList();
        if (data && data.categories) {
          setAvailableSkills(data.categories);
          console.log('Successfully loaded skills data:', data.categories);
        } else {
          throw new Error('Invalid skills data format received');
        }
      } catch (error) {
        console.error('Error loading skills data:', error);
        setError('Failed to load skills data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    loadSkillsData();
  }, []);

  // Search users when criteria change
  useEffect(() => {
    const searchUsers = async () => {
      try {
        const results = await skillsService.searchUsers({
          region: selectedRegion,
          state: selectedState,
          category: selectedCategory,
          skill: selectedSkill,
          query: searchQuery
        });
        setFilteredResults(results);
      } catch (error) {
        console.error('Error searching users and skills:', error);
        //setError('Failed to search community members. Please try again.');
      }
    };

    const delayDebounce = setTimeout(() => {
      searchUsers();
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [selectedRegion, selectedState, selectedCategory, selectedSkill, searchQuery]);

  return (
    <div className="max-w-7xl mx-auto p-6">
      <Card className="w-full mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-6 w-6" />
            Community Skills Exchange Network
          </CardTitle>
          <div className="mt-4 text-gray-600">
            <p className="mb-4">
              Our community thrives when we share our diverse skills, knowledge, and resources with each other. 
              Whether you're a craftsperson, educator, technologist, or someone with unique life experiences, 
              your skills are valuable to others. Connect with neighbors who can benefit from your expertise, 
              while discovering community members whose knowledge can help you grow.
            </p>
            <p className="mb-4">
              This Skills Exchange operates on mutual aid principles - everyone has something to contribute, 
              and everyone has something to learn. By sharing our abilities and assets, we build stronger, 
              more resilient communities where everyone's contributions are valued.
            </p>
          </div>
        </CardHeader>
        
        <CardContent>
          {error && (
            <Alert className="bg-red-50 mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-6">
            {/* Location Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Region Selection */}
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-gray-700">Region</label>
                <select
                  value={selectedRegion}
                  onChange={(e) => setSelectedRegion(e.target.value)}
                  className="w-full p-2 border rounded-md bg-white"
                >
                  <option value="">All Regions</option>
                  {Object.keys(REGIONS_AND_STATES).map(region => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
              </div>

              {/* State Selection */}
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-gray-700">State/Territory</label>
                <select
                  value={selectedState}
                  onChange={(e) => setSelectedState(e.target.value)}
                  className="w-full p-2 border rounded-md bg-white"
                  disabled={!selectedRegion}
                >
                  <option value="">All States/Territories</option>
                  {selectedRegion && REGIONS_AND_STATES[selectedRegion].map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Skill Type Selection */}
            <div className="flex flex-wrap gap-2">
              {Object.entries(CATEGORY_ICONS).map(([type, icon]) => (
                <button
                  key={type}
                  onClick={() => setSelectedSkillType(type)}
                  className={`px-4 py-2 rounded-md flex items-center gap-2 transition-colors ${
                    selectedSkillType === type
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {icon}
                  {type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </button>
              ))}
            </div>

            {/* Category and Skill Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Category Selection */}
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-gray-700">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full p-2 border rounded-md bg-white"
                  disabled={!selectedSkillType || !availableSkills[selectedSkillType] || isLoading}
                >
                  <option value="">All Categories</option>
                  {availableSkills[selectedSkillType] && 
                    Object.keys(availableSkills[selectedSkillType]).sort().map(category => (
                      <option key={category} value={category}>{category}</option>
                    ))
                  }
                </select>
              </div>

              {/* Skill Selection */}
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-gray-700">Specific Skill</label>
                <select
                  value={selectedSkill}
                  onChange={(e) => setSelectedSkill(e.target.value)}
                  className="w-full p-2 border rounded-md bg-white"
                  disabled={!selectedCategory || !availableSkills[selectedSkillType]?.[selectedCategory] || isLoading}
                >
                  <option value="">All Skills</option>
                  {selectedCategory && 
                    availableSkills[selectedSkillType]?.[selectedCategory]?.sort().map(skill => (
                      <option key={skill} value={skill}>{skill}</option>
                    ))
                  }
                </select>
              </div>

              {/* Search Input */}
              <div className="flex flex-col">
                <label className="mb-2 text-sm font-medium text-gray-700">Search</label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name or skill..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full p-2 border rounded-md pl-8"
                  />
                  <Search className="h-4 w-4 absolute left-2 top-3 text-gray-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-4 mt-6">
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading results...</p>
              </div>
            ) : filteredResults.length === 0 ? (
              <Alert className="bg-blue-50">
                <AlertDescription>
                  No matching community members found. Try adjusting your search criteria.
                </AlertDescription>
              </Alert>
            ) : (
              filteredResults.map(person => (
                <Card key={person.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold mb-2">{person.name}</h3>
                        <div className="flex items-center gap-2 text-gray-600 mb-3">
                          <MapPin className="h-4 w-4" />
                          <span>{person.state ? `${person.state}, ${person.region}` : person.region}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {person.skills.map(skill => (
                            <span
                              key={skill}
                              className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                      <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                        Connect
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SkillsExchange;